export type CookieFirstConsent = {
  necessary?: boolean;
  performance?: boolean;
  functional?: boolean;
  advertising?: boolean;
  [category: string]: boolean | undefined;
};

export type CookieFirstServicesConsent = Record<string, boolean | undefined>;

type CookieFirstConsentEvent = CustomEvent<
  CookieFirstConsent | CookieFirstServicesConsent | null
>;

const POSTHOG_CONSENT_CATEGORIES = ["advertising", "performance"];
const REQUIRED_SERVICE_KEYS = ["cookiefirst"];

const hasCategoryConsent = (consent?: CookieFirstConsent | null): boolean =>
  Boolean(
    consent && POSTHOG_CONSENT_CATEGORIES.some((category) => consent[category])
  );

const hasServiceConsent = (
  acceptedServices?: CookieFirstServicesConsent | null
): boolean =>
  Boolean(
    acceptedServices &&
      Object.entries(acceptedServices).some(
        ([serviceKey, hasConsented]) =>
          hasConsented && !REQUIRED_SERVICE_KEYS.includes(serviceKey)
      )
  );

export const hasCookieFirstAnalyticsConsent = ({
  consent,
  acceptedServices,
}: {
  consent?: CookieFirstConsent | null;
  acceptedServices?: CookieFirstServicesConsent | null;
}): boolean =>
  hasCategoryConsent(consent) || hasServiceConsent(acceptedServices);

export const hasCurrentCookieFirstAnalyticsConsent = (): boolean => {
  const cookieFirst = window.CookieFirst;

  return Boolean(
    cookieFirst?.hasConsented ||
      hasCookieFirstAnalyticsConsent({
        consent: cookieFirst?.consent,
        acceptedServices: cookieFirst?.acceptedServices,
      })
  );
};

export const onCookieFirstAnalyticsConsent = (
  onConsent: () => void
): (() => void) => {
  const handleCurrentConsent = () => {
    if (hasCurrentCookieFirstAnalyticsConsent()) {
      onConsent();
    }
  };

  const handleCategoryConsent = (event: Event) => {
    if (
      hasCookieFirstAnalyticsConsent({
        consent: (event as CookieFirstConsentEvent).detail,
        acceptedServices: window.CookieFirst?.acceptedServices,
      })
    ) {
      onConsent();
    }
  };

  const handleServicesConsent = (event: Event) => {
    if (
      hasCookieFirstAnalyticsConsent({
        consent: window.CookieFirst?.consent,
        acceptedServices: (event as CookieFirstConsentEvent).detail,
      })
    ) {
      onConsent();
    }
  };

  window.addEventListener("cf_init", handleCurrentConsent);
  window.addEventListener("cf_consent", handleCategoryConsent);
  window.addEventListener("cf_consent_loaded", handleCategoryConsent);
  window.addEventListener("cf_services_consent", handleServicesConsent);
  window.addEventListener("cf_services_consent_loaded", handleServicesConsent);

  handleCurrentConsent();

  return () => {
    window.removeEventListener("cf_init", handleCurrentConsent);
    window.removeEventListener("cf_consent", handleCategoryConsent);
    window.removeEventListener("cf_consent_loaded", handleCategoryConsent);
    window.removeEventListener("cf_services_consent", handleServicesConsent);
    window.removeEventListener(
      "cf_services_consent_loaded",
      handleServicesConsent
    );
  };
};
